/**
 * Time zone labels for Voluum flow conditions: primary list matches Voluum’s picker
 * (`voluum-flow-timezones`); unknown IANA ids fall back to Intl-derived strings.
 */

import {
  listVoluumFlowTimezoneIds,
  voluumFlowTimezoneLabel,
} from './voluum-flow-timezones';
import { VOLUUM_TIME_ZONE_BASE_NAME } from './voluum-time-zone-base-names';

/** Fixed instant (Northern summer) for Intl fallback paths. */
const REF_VOLUUM_UI = new Date(Date.UTC(2024, 6, 15, 12, 0, 0));

function rawLongOffset(id: string, at: Date): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: id,
    timeZoneName: 'longOffset',
  }).formatToParts(at);
  return parts.find((p) => p.type === 'timeZoneName')?.value?.trim() ?? 'GMT+0';
}

export function utcBracketVoluum(id: string, at = REF_VOLUUM_UI): string {
  const raw = rawLongOffset(id, at);
  if (/^GMT$/i.test(raw)) return '(UTC+00:00)';
  const m = raw.match(/^GMT([+-])(\d{1,2})(?::(\d{2}))?$/i);
  if (!m) return '(UTC+00:00)';
  const sign = m[1] === '-' ? '-' : '+';
  const hh = String(parseInt(m[2], 10)).padStart(2, '0');
  const mm = String(m[3] ? parseInt(m[3], 10) : 0).padStart(2, '0');
  return `(UTC${sign}${hh}:${mm})`;
}

function etcCutStyleLabel(id: string): string | null {
  const m = id.match(/^Etc\/GMT([+-])(\d{1,2})$/);
  if (!m) return null;
  const utcOffset = m[1] === '+' ? -parseInt(m[2], 10) : parseInt(m[2], 10);
  if (utcOffset === 0) return 'UTC';
  if (utcOffset === -12) return 'International Date Line West';
  const signStr = utcOffset >= 0 ? `+${utcOffset}` : `${utcOffset}`;
  return `Coordinated Universal Time${signStr}`;
}

function intlLongGeneric(id: string, at: Date): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: id,
      timeZoneName: 'longGeneric',
    }).formatToParts(at);
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? id;
  } catch {
    return id;
  }
}

export function voluumTimeZoneBaseName(id: string, at = REF_VOLUUM_UI): string {
  const mapped = VOLUUM_TIME_ZONE_BASE_NAME[id];
  if (mapped) return mapped;
  if (id === 'Etc/UTC' || id === 'Etc/GMT' || id === 'Etc/UCT' || id === 'Etc/Zulu')
    return 'UTC';
  if (id.startsWith('Etc/')) {
    const cut = etcCutStyleLabel(id);
    if (cut) return cut;
  }
  const generic = intlLongGeneric(id, at);
  if (!/^GMT/i.test(generic)) return generic;
  return id.replace(/_/g, ' ');
}

function formatFallbackVoluumLabel(id: string): string {
  return `${utcBracketVoluum(id)} ${voluumTimeZoneBaseName(id)}`;
}

export function formatVoluumTimeZoneLabel(id: string): string {
  const fromCatalog = voluumFlowTimezoneLabel(id);
  if (fromCatalog) return fromCatalog;
  return formatFallbackVoluumLabel(id);
}

export function formatIanaTimeZoneLabel(id: string): string {
  return formatVoluumTimeZoneLabel(id);
}

export function listVoluumOrderedTimeZones(): readonly string[] {
  return listVoluumFlowTimezoneIds();
}

/** @deprecated */
export function listSortedIanaTimeZones(): readonly string[] {
  return listVoluumOrderedTimeZones();
}
